import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, ShieldAlert } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VALID_FORLOEB = [
  "usikker", "lotus-afklaring", "lotus-72", "lotus-stabil-90", "lotus-180", "lotus-fastholdelse", "anden-ydelse",
];

// Build the initial form state from the URL on first render so the Radix Select
// receives the correct controlled value immediately (no fragile mid-mount change).
const buildInitialState = (f) => {
  let forloeb = "usikker";
  let source = "";
  let alt = "";
  let kat = [];
  let message = "";
  let henvendelse = "";
  let indsatsomraade = "";
  try {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("forloeb");
    if (q && VALID_FORLOEB.includes(q)) forloeb = q;
    source = params.get("kilde") || "";
    const a = params.get("alt");
    if (a && VALID_FORLOEB.includes(a)) alt = a;
    kat = (params.get("kat") || "").split(",").map((x) => x.trim()).filter(Boolean);
    const h = params.get("henvendelse");
    const validH = (f.henvendelseOptions || []).map((o) => o.value);
    if (h && validH.includes(h)) henvendelse = h;
    const ind = params.get("indsatsomraade");
    const validInd = (f.indsatsomraadeOptions || []).map((o) => o.value);
    if (ind && validInd.includes(ind)) indsatsomraade = ind;
    if (source === "forloebsvaelger") {
      const opt = f.forloebOptions.find((o) => o.value === forloeb);
      message = f.finderMessage.replace("{name}", opt ? opt.label : forloeb);
    } else if (source === "kommune-side" || source === "samarbejdsproces") {
      message = f.kommuneMessage;
    } else if (indsatsomraade) {
      const optA = (f.indsatsomraadeOptions || []).find((o) => o.value === indsatsomraade);
      message = (f.areaMessage || "").replace("{area}", optA ? optA.label : indsatsomraade);
    }
  } catch (_) {
    /* ignore */
  }
  return {
    name: "", email: "", phone: "", message, forloeb, organization: "", henvendelse, indsatsomraade,
    source, recommended_forloeb: source === "forloebsvaelger" ? forloeb : "", alternative_forloeb: alt, needs_summary: kat,
  };
};

const ContactForm = ({ compact = false }) => {
  const { t } = useLang();
  const f = t.kontakt.form;
  const [form, setForm] = useState(() => buildInitialState(f));
  const [loading, setLoading] = useState(false);
  const [hp, setHp] = useState(""); // honeypot
  const startRef = React.useRef(Date.now());

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onForloebChange = (v) => {
    setForm((p) => ({ ...p, forloeb: v }));
    trackEvent(EVENTS.CONTACT_FORLOEB_SELECT, { forloeb: v });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(f.required);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        hp,
        elapsed_ms: Date.now() - startRef.current,
      });
      trackEvent(EVENTS.CONTACT_SUBMIT, { forloeb: form.forloeb });
      toast.success(f.success);
      setForm(buildInitialState(f));
      startRef.current = Date.now();
    } catch (err) {
      const msg = err?.response?.data?.detail || f.error;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full rounded-xl border border-[#D3CEC0] bg-white/70 px-4 py-3 text-[#1A2E24] placeholder:text-[#5C7567]/60 outline-none focus:border-[#8EA694] focus:ring-2 focus:ring-[#8EA694]/30 transition-colors duration-300";

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="contact-form">
      <div className={compact ? "space-y-4" : "grid sm:grid-cols-2 gap-4"}>
        <div>
          <label htmlFor="cf-name" className="block text-sm font-medium text-[#3B5245] mb-1.5">{f.name}</label>
          <input id="cf-name" name="name" value={form.name} onChange={onChange} placeholder={f.namePh} className={inputClass} data-testid="contact-name" />
        </div>
        <div>
          <label htmlFor="cf-email" className="block text-sm font-medium text-[#3B5245] mb-1.5">{f.email}</label>
          <input id="cf-email" name="email" type="email" value={form.email} onChange={onChange} placeholder={f.emailPh} className={inputClass} data-testid="contact-email" />
        </div>
      </div>
      <div>
        <label htmlFor="cf-phone" className="block text-sm font-medium text-[#3B5245] mb-1.5">{f.phone}</label>
        <input id="cf-phone" name="phone" value={form.phone} onChange={onChange} placeholder={f.phonePh} className={inputClass} data-testid="contact-phone" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#3B5245] mb-1.5">{f.forloebLabel}</label>
        <Select value={form.forloeb} onValueChange={onForloebChange}>
          <SelectTrigger className="w-full rounded-xl border border-[#D3CEC0] bg-white/70 px-4 py-6 text-[#1A2E24] focus:ring-2 focus:ring-[#8EA694]/30" data-testid="contact-forloeb">
            <SelectValue placeholder={f.forloebOptions[0].label} />
          </SelectTrigger>
          <SelectContent>
            {f.forloebOptions.map((o) => (
              <SelectItem key={o.value} value={o.value} data-testid={`forloeb-option-${o.value}`}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label htmlFor="cf-org" className="block text-sm font-medium text-[#3B5245] mb-1.5">{f.organizationLabel}</label>
        <input id="cf-org" name="organization" value={form.organization} onChange={onChange} placeholder={f.organizationPh} className={inputClass} data-testid="contact-organization" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#3B5245] mb-1.5">{f.henvendelseLabel}</label>
        <Select value={form.henvendelse || undefined} onValueChange={(v) => setForm((p) => ({ ...p, henvendelse: v }))}>
          <SelectTrigger className="w-full rounded-xl border border-[#D3CEC0] bg-white/70 px-4 py-6 text-[#1A2E24] focus:ring-2 focus:ring-[#8EA694]/30" data-testid="contact-henvendelse">
            <SelectValue placeholder={f.henvendelsePh} />
          </SelectTrigger>
          <SelectContent>
            {f.henvendelseOptions.map((o) => (
              <SelectItem key={o.value} value={o.value} data-testid={`henvendelse-option-${o.value}`}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="block text-sm font-medium text-[#3B5245] mb-1.5">{f.indsatsomraadeLabel}</label>
        <Select value={form.indsatsomraade || undefined} onValueChange={(v) => setForm((p) => ({ ...p, indsatsomraade: v }))}>
          <SelectTrigger className="w-full rounded-xl border border-[#D3CEC0] bg-white/70 px-4 py-6 text-[#1A2E24] focus:ring-2 focus:ring-[#8EA694]/30" data-testid="contact-indsatsomraade">
            <SelectValue placeholder={f.indsatsomraadePh} />
          </SelectTrigger>
          <SelectContent>
            {f.indsatsomraadeOptions.map((o) => (
              <SelectItem key={o.value} value={o.value} data-testid={`indsatsomraade-option-${o.value}`}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label htmlFor="cf-message" className="block text-sm font-medium text-[#3B5245] mb-1.5">{f.message}</label>
        <textarea id="cf-message" name="message" rows={compact ? 4 : 5} value={form.message} onChange={onChange} placeholder={f.messagePh} className={`${inputClass} resize-none`} data-testid="contact-message" />
        <p className="mt-2 flex items-start gap-2 text-xs text-[#5C7567] leading-relaxed" data-testid="contact-sensitive-note">
          <ShieldAlert className="h-4 w-4 shrink-0 text-[#C47C5D] mt-0.5" />
          {f.sensitiveNote}
        </p>
      </div>
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
        <label htmlFor="cf-company-website">Website</label>
        <input
          id="cf-company-website"
          name="company_website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={hp}
          onChange={(e) => setHp(e.target.value)}
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#2D5A3F] text-white px-8 py-3.5 font-medium hover:bg-[#1A2E24] transition-colors duration-300 disabled:opacity-60"
        data-testid="contact-submit"
      >
        {loading ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> {f.sending}</>
        ) : (
          <>{f.send} <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" /></>
        )}
      </button>
    </form>
  );
};

export default ContactForm;
